'use client';

import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import {
  Anchor,
  Button,
  Group,
  SegmentedControl,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core';
import { IconArrowLeft, IconTerminal2 } from '@tabler/icons-react';
import classes from './documentation.module.css';
import {
  LOCALES,
  SECTIONS,
  UI,
  htmlLang,
  languageNames,
  type DocBlock,
  type Locale,
  type SectionId,
} from './documentation_content';

function isLocale(value: string): value is Locale {
  return (LOCALES as readonly string[]).includes(value);
}

function Block({ block, locale }: { block: DocBlock; locale: Locale }) {
  switch (block.kind) {
    case 'p':
      return (
        <Text c="#c8ccd4" style={{ lineHeight: 1.65 }}>
          {block.text[locale]}
        </Text>
      );
    case 'subheading':
      return (
        <Title order={4} c="#ffffff" mt="xs">
          {block.text[locale]}
        </Title>
      );
    case 'list':
      return (
        <Stack component="ul" gap={6} pl="lg" style={{ margin: 0 }}>
          {block.items.map((item, i) => (
            <Text key={i} component="li" c="#c8ccd4" style={{ lineHeight: 1.6 }}>
              {item[locale]}
            </Text>
          ))}
        </Stack>
      );
    case 'code':
      return (
        <div>
          <pre className={classes.code}>
            <code>{block.code}</code>
          </pre>
          {block.caption && (
            <Text className={classes.caption}>{block.caption[locale]}</Text>
          )}
        </div>
      );
    case 'table':
      return (
        <Table
          striped
          withTableBorder
          verticalSpacing="sm"
          horizontalSpacing="md"
        >
          <Table.Thead>
            <Table.Tr>
              {block.head.map((h, i) => (
                <Table.Th key={i}>{h[locale]}</Table.Th>
              ))}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {block.rows.map((row, r) => (
              <Table.Tr key={r}>
                {row.map((cell, c) => (
                  <Table.Td
                    key={c}
                    className={c === 0 ? classes.mono : undefined}
                  >
                    {cell[locale]}
                  </Table.Td>
                ))}
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      );
  }
}

export default function DocumentationClient() {
  const [locale, setLocale] = useState<Locale>('en');
  const [activeId, setActiveId] = useState<SectionId>(SECTIONS[0].id);
  const rootRef = useRef<HTMLDivElement | null>(null);

  // Restore a saved language preference after mount (keeps SSR markup stable).
  useEffect(() => {
    const saved = window.localStorage.getItem('echo-docs-locale');
    if (saved && isLocale(saved)) setLocale(saved);
  }, []);

  useEffect(() => {
    window.localStorage.setItem('echo-docs-locale', locale);
    document.documentElement.lang = htmlLang[locale];
  }, [locale]);

  // Scrollspy: highlight the in-page nav entry for the section in view. The
  // observer roots on the internally-scrolling shell, not the document.
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]) {
          const id = visible[0].target.id as SectionId;
          setActiveId((prev) => (prev === id ? prev : id));
        }
      },
      { root, rootMargin: '0px 0px -60% 0px', threshold: [0.1, 0.5, 1] }
    );
    for (const section of SECTIONS) {
      const el = document.getElementById(section.id);
      if (el) observer.observe(el);
    }
    return () => observer.disconnect();
  }, []);

  return (
    <div className={classes.root} ref={rootRef}>
      <Anchor href="#doc-content" className={classes.skipLink}>
        Skip to content
      </Anchor>
      <div className={classes.inner}>
        <div className={classes.header}>
          <div className={classes.headerText}>
            <Title order={1} c="#ffffff">
              {UI.pageTitle[locale]}
            </Title>
            <Text size="sm" c="#b8b8b8" mt={6} maw={640}>
              {UI.pageSubtitle[locale]}
            </Text>
            <Group gap="sm" mt="md">
              <Button
                component={Link}
                href="/"
                variant="light"
                color="gray"
                size="xs"
                leftSection={<IconArrowLeft size={15} />}
              >
                {UI.backToDemo[locale]}
              </Button>
              <Button
                component={Link}
                href="/dev"
                variant="subtle"
                color="gray"
                size="xs"
                leftSection={<IconTerminal2 size={15} />}
              >
                {UI.openDev[locale]}
              </Button>
            </Group>
          </div>
          <div className={classes.controls}>
            <Text size="xs" c="#8a8a8a">
              {UI.languageLabel[locale]}
            </Text>
            <SegmentedControl
              value={locale}
              onChange={(value) => {
                if (isLocale(value)) setLocale(value);
              }}
              size="sm"
              data={LOCALES.map((l) => ({ value: l, label: languageNames[l] }))}
              aria-label={UI.languageLabel[locale]}
            />
          </div>
        </div>

        <div className={classes.layout}>
          <nav className={classes.toc} aria-label={UI.onThisPage[locale]}>
            <p className={classes.tocHeading}>{UI.onThisPage[locale]}</p>
            {SECTIONS.map((section) => (
              <a
                key={section.id}
                href={`#${section.id}`}
                className={`${classes.tocLink} ${
                  activeId === section.id ? classes.tocLinkActive : ''
                }`}
                aria-current={activeId === section.id ? 'true' : undefined}
              >
                {section.title[locale]}
              </a>
            ))}
          </nav>

          <main id="doc-content" className={classes.content}>
            {SECTIONS.map((section) => (
              <section
                key={section.id}
                id={section.id}
                className={classes.section}
                aria-labelledby={`${section.id}-title`}
              >
                <Title
                  id={`${section.id}-title`}
                  order={2}
                  c="#ffffff"
                  mt="lg"
                  mb="sm"
                >
                  {section.title[locale]}
                </Title>
                <Stack gap="sm">
                  {section.blocks.map((block, i) => (
                    <Block key={i} block={block} locale={locale} />
                  ))}
                </Stack>
              </section>
            ))}
          </main>
        </div>
      </div>
    </div>
  );
}
